import { chainConstants, readAssetValue } from '@chainflip/utils/chainflip';
import { intervalToDurationWords } from '@chainflip/utils/date';
import { Link } from '@/shared/components/atoms/Link';
import { SkeletonLine } from '@/shared/components/atoms/LoadingSkeleton';
import QuestionMarkTooltip from '@/shared/components/QuestionMarkTooltip';
import { useRpcChainflipBlockConfirmations } from '@/shared/hooks/useRpcChainflipBlockConfirmations';
import type { DepositChannel } from '@/shared/hooks/useStateChainAccount';
import useTokenPrice from '@/shared/hooks/useTokenPrice';
import ArrowIcon from '@/shared/icons/flip-ui-kit/small/Arrow';
import ClockIcon from '@/shared/icons/flip-ui-kit/small/Clock';
import FeeIcon from '@/shared/icons/flip-ui-kit/small/Fee';
import QRIcon from '@/shared/icons/flip-ui-kit/small/QR';
import TimeBoost from '@/shared/icons/flip-ui-kit/small/TimeBoost';
import { formatUsdValue } from '@/shared/utils/numbers';
import TokenAmount from '@/shared/utils/TokenAmount';
import { useBoostChainflipBlocksDelay } from 'packages/liquidity-provision/hooks/useBoostChainflipBlocksDelay';
import useIngressEgressEnv from '../../../hooks/useEnvironment';

const indexerDelayDuration = 18; // 3 statechain blocks before block is finalized

export const DepositInformation = ({
  channel,
  showMinDepositAmount,
  showExplorerLink,
}: {
  channel: DepositChannel;
  showMinDepositAmount: boolean;
  showExplorerLink: boolean;
}) => {
  const chain = channel.asset.chain.chainflipId;
  const { price } = useTokenPrice(channel.asset);
  const { data: blockConfirmations } = useRpcChainflipBlockConfirmations();
  const { data: boostChainflipBlocksDelayMap } = useBoostChainflipBlocksDelay(chain);
  const { ingressFees, minimumDepositAmounts, isLoading: isEnvLoading } = useIngressEgressEnv();
  const isBoostedChannel = Boolean(channel.boostFee);

  const ingressFee =
    ingressFees &&
    TokenAmount.fromAsset(
      readAssetValue(ingressFees, channel.asset.chainflipId) ?? 0,
      channel.asset.chainflipId,
    );
  const ingressFeeUsd = price && ingressFee?.mul(price).toNumber();

  const tokenMinimumDepositAmount = minimumDepositAmounts
    ? readAssetValue(minimumDepositAmounts, channel.asset.chainflipId)
    : 0n;

  const minimumDepositAmount = new TokenAmount(
    tokenMinimumDepositAmount.toString(),
    channel.asset.decimals,
  );
  const minimumDepositAmountUsd = price && minimumDepositAmount?.mul(price).toNumber();

  const regularConfirmationSeconds = blockConfirmations
    ? (blockConfirmations[chain] ?? 1) * chainConstants[chain].blockTimeSeconds
    : undefined;

  const witnessSubmissionDuration = 6 + (boostChainflipBlocksDelayMap?.[chain] ?? 0) * 6; // 1 statechain block for witnessing + boost delay in seconds

  const regularEstimatedDuration =
    regularConfirmationSeconds &&
    intervalToDurationWords({
      start: 0,
      end: (regularConfirmationSeconds + witnessSubmissionDuration + indexerDelayDuration) * 1000,
    });

  const boostConfirmationSeconds = blockConfirmations
    ? chainConstants[chain].blockTimeSeconds
    : undefined;

  const boostEstimatedDuration =
    boostConfirmationSeconds &&
    intervalToDurationWords({
      start: 0,
      end: (boostConfirmationSeconds + witnessSubmissionDuration + indexerDelayDuration) * 1000,
    });

  const estimatedDuration = isBoostedChannel ? boostEstimatedDuration : regularEstimatedDuration;

  return (
    <div className="flex flex-col gap-3 rounded-md border border-cf-gray-4 bg-cf-gray-3-5 p-4 font-aeonikMedium text-14 text-cf-light-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <ClockIcon />
          <span>Estimated time</span>
          {/* <QuestionMarkTooltip content="TODO(1.9)" /> */}
        </div>
        {!estimatedDuration ? (
          <SkeletonLine count={1} width={100} />
        ) : (
          <div className="flex items-center gap-1 text-cf-light-3">
            {isBoostedChannel && <TimeBoost className="text-cf-pink-1" />}
            <span>{estimatedDuration}</span>
            {isBoostedChannel && (
              <span className="text-12 text-cf-light-2 line-through">
                {regularEstimatedDuration}
              </span>
            )}
          </div>
        )}
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <FeeIcon />
          <span>Deposit fee</span>
          <QuestionMarkTooltip
            content="Network gas charge to collect funds from the deposit address."
            link="https://docs.chainflip.io/protocol/token-economics/incentive-design-emission-and-burning#state-chain-gas-fees"
          />
        </div>
        <div>
          {!ingressFee ? (
            <SkeletonLine count={1} width={100} />
          ) : (
            <span>
              <span className="text-cf-light-3">
                {ingressFee.toFormatted()} {channel.asset.symbol}
              </span>{' '}
              {ingressFeeUsd !== undefined && `(${formatUsdValue(ingressFeeUsd)})`}
            </span>
          )}
        </div>
      </div>
      {showMinDepositAmount && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <ArrowIcon direction="down" />
            <span>Min. deposit amount</span>
            <QuestionMarkTooltip content="Deposit amounts below this value are absorbed by the network." />
          </div>
          <div>
            {isEnvLoading ? (
              <SkeletonLine count={1} width={100} />
            ) : (
              <span>
                <span className="text-cf-light-3">
                  {minimumDepositAmount?.toFormatted()} {channel.asset.symbol}
                </span>{' '}
                {minimumDepositAmountUsd !== undefined &&
                  `(${formatUsdValue(minimumDepositAmountUsd)})`}
              </span>
            )}
          </div>
        </div>
      )}
      {showExplorerLink && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <QRIcon />
            <span>Liquidity Channel</span>
          </div>
          <div>
            <Link
              href={new URL(
                `/channels/${channel.blockNumber}-${channel.asset.chain.chainflipId}-${channel.channelId}`,
                process.env.NEXT_PUBLIC_EXPLORER_URL,
              ).toString()}
              underline
              target="_blank"
            >
              {channel.blockNumber}-{channel.asset.chain.chainflipId}-{channel.channelId}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};
